import { FunctionComponent } from 'react'
import { Chip, Link, Stack, Typography } from '@mui/material'
import { OpenInNew } from '@mui/icons-material'
import { IReductionActionContent } from '../../../shared/services/reduction-action/reduction-action.model'
import { formatCostBand, formatGbpRange, formatPaybackRange, formatPctRange, SCOPE2_BASIS_LABELS } from '../../../shared/utils/reduction-utils'

// What a library action says about itself: the typical saving range, what it is a share of,
// the cost band and payback, and the public source. Shared by the library card (compact) and
// the detail dialog (full). Figures are published starting points, never measured, and the
// copy says so.

const SAVING_BASIS_LABELS: Record<string, string> = {
  pool_total: 'of the emissions it applies to',
  category_total: 'of the emissions in its category',
}

export const ReductionActionContentSummary: FunctionComponent<{
  action: IReductionActionContent,
  variant?: 'compact' | 'full',
}> = ({ action, variant = 'compact' }) => {
  const hasSaving = action.savingBasis && action.savingBasis !== 'none' && action.savingMinPct != null && action.savingMaxPct != null
  const costLabel = formatCostBand(action.costBand)
  const capex = formatGbpRange(action.capexMinGbp, action.capexMaxGbp)
  const payback = formatPaybackRange(action.paybackYearsMin, action.paybackYearsMax)
  const isDataAction = action.savingBasis === 'none'

  if (!hasSaving && !costLabel && !isDataAction) return null

  if (variant === 'compact') {
    return (
      <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap' useFlexGap sx={{ mt: 1 }}>
        {hasSaving && (
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>
            Typically saves {formatPctRange(action.savingMinPct, action.savingMaxPct)} {SAVING_BASIS_LABELS[action.savingBasis || 'pool_total']}
          </Typography>
        )}
        {isDataAction && (
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>Improves data quality; no saving claimed</Typography>
        )}
        {costLabel && <Chip label={costLabel} size='small' variant='outlined' />}
      </Stack>
    )
  }

  return (
    <Stack spacing={1} sx={{ mt: 1 }}>
      {hasSaving && (
        <Typography variant='body2'>
          <b>Typical saving:</b> {formatPctRange(action.savingMinPct, action.savingMaxPct)} {SAVING_BASIS_LABELS[action.savingBasis || 'pool_total']}
          {action.scope === '2' && action.scope2Basis && action.scope2Basis !== 'both' && ` (${SCOPE2_BASIS_LABELS[action.scope2Basis].toLowerCase()})`}
        </Typography>
      )}
      {isDataAction && (
        <Typography variant='body2'><b>Saving:</b> none claimed. This action improves the accuracy of your figures.</Typography>
      )}
      {(costLabel || capex || payback) && (
        <Typography variant='body2'>
          <b>Cost:</b> {[costLabel, capex, payback ? `payback ${payback}` : ''].filter(Boolean).join(', ')}
        </Typography>
      )}
      {action.sourceReference && (
        <Typography variant='body2' sx={{ color: 'text.secondary' }}>
          <b>Source:</b> {action.sourceReference}
          {action.sourceUrl && (
            <>
              {' '}
              <Link href={action.sourceUrl} target='_blank' rel='noopener noreferrer' underline='hover'>
                View <OpenInNew sx={{ fontSize: '0.9rem', verticalAlign: 'middle' }} />
              </Link>
            </>
          )}
        </Typography>
      )}
      <Typography variant='caption' sx={{ color: 'text.secondary' }}>
        Figures are typical ranges from published guidance, not measurements. You can change any figure you add to your plan.
      </Typography>
    </Stack>
  )
}
